/* BCF add-to-bag → Horizon cart drawer (Phase 2 cart wiring).
   ES module: dispatches Horizon's own CartLinesUpdateEvent (from the theme
   importmap's @shopify/events) so the existing cart drawer auto-opens, the
   drawer items morph, and the header cart bubble updates — no cart rebuilt.
   Intercepts any BCF form posting to /cart/add (specimen card quick-add +
   PDP buy box). Every failure path falls back to the native form post. */
import { CartLinesUpdateEvent } from '@shopify/events';
import { requestedLines, confirmedAddedLines, lineTotalCents, isQaAction } from './bcf-cart-attribution.js';

const routes = (window.Theme && window.Theme.routes) || {};
const ADD_URL = routes.cart_add_url || '/cart/add';
const CART_URL = routes.cart_url || '/cart';
const activeForms = new WeakSet();
let checkoutStartCaptured = false;
let addQueue = Promise.resolve();

function eventId(prefix) {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function posthogDistinctId() {
  try {
    if (window.posthog && typeof window.posthog.get_distinct_id === 'function') {
      const existing = window.posthog.get_distinct_id();
      if (existing) return existing;
    }
    let fallback = window.sessionStorage.getItem('bcf_checkout_anon_id') || '';
    if (!fallback) {
      fallback = eventId('bcf-checkout-anon');
      window.sessionStorage.setItem('bcf_checkout_anon_id', fallback);
    }
    return fallback;
  } catch (error) {
    return eventId('bcf-checkout-anon');
  }
}

function trackCheckoutStart(container) {
  if (checkoutStartCaptured || isQaAction(null, null) || container.dataset.bcfCheckoutQa === 'true') return;
  const itemCount = Number(container.dataset.bcfCheckoutItemCount) || 0;
  if (!itemCount) return;
  checkoutStartCaptured = true;

  const signature = container.dataset.bcfCheckoutSignature || '';

  let items = [];
  try {
    items = JSON.parse(container.dataset.bcfCheckoutItems || '[]');
  } catch (error) {}

  const checkoutEventId = eventId('bcf-checkout');
  const payload = {
    api_key: 'phc_xKKHByK2YkyaePP5B5JxfFXdfEpsyX3sqz2ZxhR4dWa5',
    event: 'checkout_started',
    distinct_id: posthogDistinctId(),
    properties: {
      $process_person_profile: false,
      $current_url: window.location.href,
      $insert_id: checkoutEventId,
      checkout_event_id: checkoutEventId,
      cart_signature: signature,
      item_count: itemCount,
      line_count: items.length,
      value: (Number(container.dataset.bcfCheckoutValue) || 0) / 100,
      currency: container.dataset.bcfCheckoutCurrency || 'USD',
      items,
      path: window.location.pathname,
      source: 'shopify_storefront_checkout_handoff',
    },
  };
  const body = JSON.stringify(payload);
  const endpoint = 'https://us.i.posthog.com/i/v0/e/';
  const sent = typeof window.navigator.sendBeacon === 'function'
    ? window.navigator.sendBeacon(endpoint, body)
    : false;
  if (!sent) {
    window.fetch(endpoint, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      keepalive: true,
    }).catch(() => {});
  }

  if (window.Shopify?.analytics?.publish) {
    window.Shopify.analytics.publish('bcf:checkout_handoff', payload.properties);
  }
}

function trackCheckoutFromEvent(event) {
  const submitter = event.composedPath().find(
    (node) => node instanceof Element && node.matches('[name="checkout"]')
  );
  if (!submitter) return;
  const container = submitter.closest('[data-bcf-checkout]');
  if (container instanceof HTMLElement) trackCheckoutStart(container);
}

/* Listen at the document boundary so Shopify's cart components cannot race
   the analytics handoff. Pointer, keyboard and synthetic clicks collapse to
   one event through checkoutStartCaptured, while checkout remains native. */
document.addEventListener('pointerdown', trackCheckoutFromEvent, true);
document.addEventListener('click', trackCheckoutFromEvent, true);
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Enter' || event.key === ' ') trackCheckoutFromEvent(event);
  },
  true
);

function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids;
}

async function addToCart(form) {
  activeForms.add(form);
  let addRequestSucceeded = false;
  let addRequestAttempted = false;
  let explicitlyRejected = false;
  const formData = new FormData(form);
  if (isQaAction(formData, null)) {
    if (formData.get('id')) formData.set('properties[_bcf_qa]', 'explicit-qa');
    for (let index = 0; index < 20; index += 1) {
      if (formData.get(`items[${index}][id]`)) formData.set(`items[${index}][properties][_bcf_qa]`, 'explicit-qa');
    }
  }
  const ids = sectionIds();
  if (ids.length) formData.append('sections', ids.join(','));

  const atcActionId = eventId('bcf-atc');
  if (['pair', 'trio'].includes(String(formData.get('properties[_bcf_offer]') || ''))) {
    // Distinguish separately added native parents, including identical bundles.
    formData.set('properties[_bcf_bundle_group]', atcActionId);
  }
  const requested = requestedLines(formData);
  const quantity = requested.reduce((sum, line) => sum + line.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: requested.map((line) => ({ merchandiseId: line.id, quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  const submit = form.querySelector('[type="submit"]');
  submit?.setAttribute('disabled', '');

  try {
    const beforeRes = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!beforeRes.ok) throw new Error('Cart state could not be read before add.');
    const beforeCart = await beforeRes.json();
    if(form.dataset.bcfOffer==='pair'&&beforeCart.items.some(item=>item.properties?._bcf_role==='addon')){
      const status=form.closest('.bcf')?.querySelector('#selection-error');if(status){status.hidden=false;status.textContent='Your add-on is already in your bag. Change its fit or quantity in the bag.';}
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(beforeCart),detail:{items:beforeCart.items,source:'bcf-cart',itemCount:0,didError:false}});return;
    }
    addRequestAttempted = true;
    const res = await fetch(ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    addRequestSucceeded = res.ok;
    explicitlyRejected = !res.ok;
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.message || 'Add to cart failed');

    const cartRes = await fetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } });
    if (!cartRes.ok) throw new Error('Cart refresh failed.');
    const ajaxCart = await cartRes.json();

    // Shopify's final cart is authoritative. Failure to attribute must not
    // invalidate a successful add or emit a guessed revenue event.
    if (!isQaAction(formData, ajaxCart)) {
      try {
        const addedItems = confirmedAddedLines(requested, data, ajaxCart, beforeCart);
        const line = addedItems[0] || {};
        const itemCount = Number(ajaxCart.item_count) || 0;
        const itemCountBefore = Number(beforeCart.item_count) || 0;
        const isFirstCartBuild = itemCountBefore === 0 && itemCount > 0;
        document.dispatchEvent(new CustomEvent('bcf:add-to-cart', {
          detail: {
            atcActionId,
            cartBuildId: isFirstCartBuild ? eventId('bcf-cart-build') : '',
            isFirstCartBuild,
            itemCountBefore,
            itemCountAfter: itemCount,
            variantId: String(line.variant_id || line.id || ''),
            quantity: line.quantity || 1,
            item: line,
            items: addedItems,
            totalQuantity: quantity,
            valueCents: addedItems.reduce((sum, item) => sum + lineTotalCents(item), 0),
            currency: ajaxCart.currency || 'USD',
          },
        }));
      } catch (attributionError) {
        console.warn('[BCF] Confirmed cart updated; custom attribution skipped.', attributionError);
      }
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'bcf-cart',
        itemCount: quantity,
        sections: data.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    if (addRequestSucceeded || (addRequestAttempted && !explicitlyRejected)) {
      // Shopify accepted the item, or acceptance is unknown. Never post
      // the same form again: that could add a duplicate line. The cart page
      // reads Shopify's confirmed cart state without another mutation.
      window.location.assign(CART_URL);
    } else {
      // The add itself did not succeed, so the native post is still a safe
      // recovery path and preserves Shopify's own error handling.
      form.submit();
    }
  } finally {
    if (form.dataset.bcfSelectionValid !== 'false') submit?.removeAttribute('disabled');
    activeForms.delete(form);
    form.dataset.bcfAddBusy = 'false';
    document.dispatchEvent(new CustomEvent('bcf:cart-add-state', { detail: { formId: form.getAttribute('id'), busy: false } }));
  }
}

// Capture phase (3rd arg `true`) is load-bearing: Shopify's shop_events_listener
// wires a target-phase "submit" listener on every /cart/add form that emits a
// `product_added_to_cart` web-pixel event UNLESS event.defaultPrevented is already
// true. Preventing default here in the capture phase (before that target-phase
// listener runs) suppresses its "add-form" emission, so the only add_to_cart left
// is the one Shopify's fetch patch emits for our /cart/add.js POST — exactly one.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('.bcf')) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    if (!document.querySelector('cart-drawer-component')) return; // native flow
    event.preventDefault();
    if (form.dataset.bcfSelectionValid === 'false') {
      const status = form.parentElement?.querySelector('.minimal-add-status');
      if (status) status.textContent = 'Choose available lengths before adding this selection.';
      return;
    }
    if (activeForms.has(form)) return;
    activeForms.add(form);
    form.dataset.bcfAddBusy = 'true';
    document.dispatchEvent(new CustomEvent('bcf:cart-add-state', { detail: { formId: form.getAttribute('id'), busy: true } }));
    form.querySelector('[type="submit"]')?.setAttribute('disabled', '');
    // Serialize custom adds so before/after cart-build attribution is exact.
    addQueue = addQueue.then(() => addToCart(form)).catch(() => {});
  },
  true
);
